import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ShieldCheck, UsersRound } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { ClearanceSummary } from "@/components/clients/ClearanceSummary";
import { ClientPacketList } from "@/components/clients/ClientPacketList";
import { api, formatApiError } from "@/lib/api";

export default function ClientAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clearance, setClearance] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadClientAssignments() {
      try {
        const [assignmentResponse, requestResponse, clearanceResponse] = await Promise.all([
          api.get("/assignments"),
          api.get("/requests"),
          api.get("/compliance/clearance").catch(() => ({ data: null })),
        ]);
        setAssignments(assignmentResponse.data);
        setRequests(requestResponse.data);
        setClearance(clearanceResponse.data);
      } catch (requestError) {
        setError(formatApiError(requestError));
      } finally {
        setLoaded(true);
      }
    }
    loadClientAssignments();
  }, []);

  const requestRoles = useMemo(
    () => new Map(requests.map((request) => [request.id, request.role_name])),
    [requests],
  );
  const clearanceByAssignment = useMemo(
    () => new Map((clearance?.providers || []).map((row) => [row.assignment_id, row])),
    [clearance],
  );

  return (
    <div className="app-shell" data-testid="client-assignments-shell">
      <AppSidebar activeItem="Providers & Assignments" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page client-portal-page" data-testid="client-assignments-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">YOUR TAG TEAM</p>
              <h1>Providers & Assignments</h1>
              <p className="page-subtitle">
                See the TAG professionals assigned to your organization and their service status.
              </p>
            </div>
            <div className="client-page-count" data-testid="client-assignment-count">
              <UsersRound size={18} />
              <strong>{assignments.length}</strong>
              <span>active assignments</span>
            </div>
          </section>
          {error && (
            <p className="form-message error-message" data-testid="client-assignments-error">
              {error}
            </p>
          )}
          <section className="client-assignment-grid" data-testid="client-assignment-grid">
            {assignments.map((assignment) => (
              <article
                className="client-assignment-card"
                data-testid={`client-assignment-${assignment.id}`}
                key={assignment.id}
              >
                <div className="client-assignment-card-header">
                  <span className="client-provider-initials" aria-hidden="true">
                    {assignment.candidate_name
                      .split(" ")
                      .map((name) => name[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                  <div>
                    <p className="eyebrow">TAG PROVIDER</p>
                    <h2 data-testid={`client-assignment-provider-${assignment.id}`}>
                      {assignment.candidate_name}
                    </h2>
                  </div>
                  <span className={`client-status client-status-${assignment.status.toLowerCase()}`}>
                    {assignment.status}
                  </span>
                </div>
                <dl className="client-assignment-details">
                  <div>
                    <dt>Service</dt>
                    <dd>{requestRoles.get(assignment.request_id) || "TAG service assignment"}</dd>
                  </div>
                  <div>
                    <dt>Service dates</dt>
                    <dd>
                      <CalendarDays size={15} />
                      {assignment.start_date} — {assignment.end_date || "Ongoing"}
                    </dd>
                  </div>
                </dl>
                <ClearanceSummary summary={clearanceByAssignment.get(assignment.id)} />
              </article>
            ))}
            {!loaded && !assignments.length && !error && <div className="client-empty-state list-loading" data-testid="client-assignments-loading">Loading assignments…</div>}
            {loaded && !assignments.length && !error && (
              <div className="client-empty-state" data-testid="client-assignments-empty">
                <ShieldCheck size={24} />
                <div>
                  <h2>Your provider roster will appear here</h2>
                  <p>TAG will update this workspace when a provider is confirmed for your services.</p>
                </div>
              </div>
            )}
          </section>
          <ClientPacketList packets={clearance?.packets} />
        </main>
      </div>
    </div>
  );
}